import { AlchemyStar } from '@creditornot/cb-icons';
import { cbSemanticCssVars, colors, typographyCss } from '@creditornot/cb-ingredients';
import { LegacyPopover } from '@creditornot/cb-popover';
import { Tag } from '@creditornot/cb-tag';
import React, { useCallback, useEffect, useState } from 'react';
import { Field, useForm, useFormState } from 'react-final-form';
import toast from 'react-hot-toast';
import { useIntl } from 'react-intl';
import styled, { css } from 'styled-components';

import { useOfferingMenuPermissionsContext } from 'modules/shared-menu/contexts/OfferingMenuPermissionsContext';
import { useSelectionData } from 'modules/shared-menu/hooks/useSelectionData';

import { BaseField } from './types';
import { useProductOverridesContext } from '../../../context/ProductOverridesContext';
import { GenerateDescriptionArgs, useAiDescriptions } from '../../../hooks/useAiDescriptions';
import { FormRowRenderer } from '../FormRowRenderer';
import { GenerateDescriptionModal } from '../GenerateDescriptionModal';
import { DisplayText, StyledInputContainer, StyledTextarea } from '../OfferDetailsFormFields.styles';
import { DESCRIPTION_MAX_LENGTH } from '../constants';
import { BasicDetails, FormValues } from '../types';
import { ProductHierarchyOption, getCategoryOptions } from '../utils';

type DescriptionProps = BaseField & {
  primaryLanguage: Language;
  productHierarchyOptions: ProductHierarchyOption[];
};

const ClickableTag = styled(Tag).attrs({ role: 'button' })<{ $disabled?: boolean }>`
  cursor: pointer;

  &:hover {
    opacity: 0.8;
    background: ${colors.nightSky16};
  }

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      &:hover {
        opacity: 1;
        background: ${colors.nightSky8};
      }
    `}
`;

const PopoverContent = styled.div`
  display: flex;
  flex-direction: column;
  padding: 6px 10px;
  max-width: 265px;
  max-height: 600px;
  overflow-y: auto;

  ${typographyCss.Small()};
  color: ${cbSemanticCssVars.textPrimary};
`;

export const Description = ({
  sidePanelMode,
  offer,
  primaryLanguage,
  menuLocalizer,
  productHierarchyOptions,
}: DescriptionProps) => {
  const menuPermissions = useOfferingMenuPermissionsContext();
  const intl = useIntl();
  const { categories } = useSelectionData();
  const { values } = useFormState<FormValues>();
  const form = useForm();
  const { mutateAsync: generateAiDescription, isLoading: isGeneratingAiDescription } = useAiDescriptions();
  const [isGenerateDescriptionModalOpen, setIsGenerateDescriptionModalOpen] = useState(false);
  const [generatedDescription, setGeneratedDescription] = useState('');
  const { isAttrConnectedToProduct } = useProductOverridesContext();
  const isConnectedToCatalog = isAttrConnectedToProduct('description');

  const unsubscribe = form.subscribe(
    ({ values }) => {
      const userDidDeleteDescription =
        (!values.description || (typeof values.description === 'string' && !values.description?.trim())) &&
        !!generatedDescription;

      if (userDidDeleteDescription) {
        setGeneratedDescription('');
      }
    },
    { values: true },
  );

  useEffect(() => {
    return () => {
      unsubscribe();
    };
  }, [unsubscribe]);

  const hasEnoughInitialInfo = !!values.description;

  const buildInitialPrompt = useCallback(() => {
    const prompt: (string | null)[] = [values.name ?? null, values.description ?? null];

    if (values.categoryId) {
      const categoryOptions = getCategoryOptions({ categories, primaryLanguage });
      const categoryDisplayName = categoryOptions.find(co => co.value === values.categoryId)?.label ?? null;
      prompt.push(categoryDisplayName);
    }

    if (values.productHierarchyLevelId) {
      const productFamilyDisplayName =
        productHierarchyOptions.find(pho => pho.value === values.productHierarchyLevelId)?.label ?? null;
      prompt.push(productFamilyDisplayName);
    }

    return prompt.filter(Boolean).join(' - ');
  }, [
    categories,
    primaryLanguage,
    productHierarchyOptions,
    values.categoryId,
    values.description,
    values.name,
    values.productHierarchyLevelId,
  ]);

  const handleGenerateClick = useCallback(
    async ({ info, type }: Omit<GenerateDescriptionArgs, 'targetLanguage'>) => {
      try {
        const { generated } = await generateAiDescription({ targetLanguage: primaryLanguage, info, type });

        if (!generated || typeof generated !== 'string') {
          toast.error(intl.formatMessage({ id: 'menu-item.ai-description.generation-error-message' }), {
            duration: 5000,
          });
          return;
        }
        const generatedWithoutQuotes = generated.replace(/^"(.*)"$/, '$1');
        form.change('description', generatedWithoutQuotes);
        setGeneratedDescription(generatedWithoutQuotes);
        setIsGenerateDescriptionModalOpen(true);
      } catch (error) {
        toast.error(intl.formatMessage({ id: 'menu-item.ai-description.generation-error-message' }), {
          duration: 5000,
        });
      }
    },
    [form, intl, generateAiDescription, primaryLanguage],
  );

  const handleOnConfirmClick = useCallback(
    (chosenDescription: string) => {
      form.change('description', chosenDescription);
      setIsGenerateDescriptionModalOpen(false);
    },
    [form],
  );

  const handleOnCancel = useCallback(() => {
    form.change('description', menuLocalizer(offer?.description));
    setIsGenerateDescriptionModalOpen(false);
    setGeneratedDescription('');
  }, [form, menuLocalizer, offer?.description]);

  const handleGenerateInitialDescriptionClick = useCallback(() => {
    handleGenerateClick({
      info: buildInitialPrompt(),
    });
  }, [buildInitialPrompt, handleGenerateClick]);

  const AiDescription = (
    <LegacyPopover
      disabled={hasEnoughInitialInfo}
      placement="left"
      content={
        <PopoverContent>
          {intl.formatMessage({ id: 'menu-item.ai-description.add-initial-description' })}
        </PopoverContent>
      }
    >
      <ClickableTag
        variant={hasEnoughInitialInfo ? 'lightDarkBlue' : 'lightBlack'}
        $disabled={!hasEnoughInitialInfo}
        icon={<AlchemyStar />}
        onClick={handleGenerateInitialDescriptionClick}
      >
        {isGeneratingAiDescription
          ? intl.formatMessage({ id: 'state.loading' })
          : !!generatedDescription
            ? intl.formatMessage({ id: 'menu-item.ai-description.refine-button' })
            : intl.formatMessage({ id: 'menu-item.ai-description.generate-button' })}
      </ClickableTag>
    </LegacyPopover>
  );

  const previewComponent = <DisplayText>{values.description || '-'}</DisplayText>;

  const editComponent = (
    <>
      <Field<BasicDetails['description']> name="description">
        {field => {
          return (
            <StyledInputContainer characterCount={{ value: field.input.value.length, max: DESCRIPTION_MAX_LENGTH }}>
              <StyledTextarea
                {...field.input}
                maxLength={DESCRIPTION_MAX_LENGTH}
                placeholder={intl.formatMessage({ id: 'menu.placeholder-unset' })}
                // Show a minimum of 2 rows to visually distinguish description from other inputs when it's empty
                rows={2}
                spellCheck
                disabled={isConnectedToCatalog || isGeneratingAiDescription}
              />
            </StyledInputContainer>
          );
        }}
      </Field>
      {isConnectedToCatalog ? null : AiDescription}
    </>
  );

  return (
    <>
      <FormRowRenderer
        hasContentEditPermission={menuPermissions.contentEdit}
        label={intl.formatMessage({ id: 'menu-item.description' })}
        previewComponent={previewComponent}
        editComponent={editComponent}
        shouldDisplayCreateTag={!values.description}
        sidePanelMode={sidePanelMode}
        offer={offer}
        catalogName="description"
      />
      {isGenerateDescriptionModalOpen && (
        <GenerateDescriptionModal
          onCancel={handleOnCancel}
          onConfirm={handleOnConfirmClick}
          onGenerate={handleGenerateClick}
          generatedDescription={generatedDescription}
          isLoading={isGeneratingAiDescription}
        />
      )}
    </>
  );
};
